import React, { useMemo } from "react";
import {
  Breakpoint,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  NESTED_DIALOG_POPPER_Z_INDEX,
  NESTED_DIALOG_Z_INDEX,
} from "components/ModalDialog/ModalDialogBase";

export interface ModalConfirmDialogProps {
  open: boolean;
  isWarning?: boolean;
  title?: string;
  maxWidth?: false | Breakpoint;
  content: string | React.ReactNode;
  defaultButton?: "OK" | "CANCEL";
  cancelButtonText?: string;
  okButtonText?: string;
  onCancelButtonClick: () => void;
  onOKButtonClick: () => void;
  cancelButtonDisabled?: boolean;
  okButtonDisabled?: boolean;
  forceOnTop?: boolean;
}

//TODO MRU 2025-12-22:  This component should be replaced everywhere it is used
// with either ModalConfirmDialogSimple or ModalConfirmDialogStatus
export const ModalConfirmDialog: React.FC<ModalConfirmDialogProps> = ({
  open,
  isWarning = false,
  title = "",
  maxWidth = false,
  content = "",
  defaultButton = "CANCEL",
  cancelButtonText = "Cancel",
  okButtonText = "OK",
  onCancelButtonClick,
  onOKButtonClick,
  cancelButtonDisabled = false,
  okButtonDisabled = false,
  forceOnTop = false,
}) => {
  const theme = useTheme();
  const forceOnTopTheme = useMemo(
    () =>
      createTheme(theme, {
        zIndex: {
          modal: NESTED_DIALOG_POPPER_Z_INDEX,
          tooltip: NESTED_DIALOG_POPPER_Z_INDEX,
        },
      }),
    [theme]
  );

  const renderedContent =
    typeof content === "string" ? (
      <DialogContentText>{content}</DialogContentText>
    ) : forceOnTop ? (
      <ThemeProvider theme={forceOnTopTheme}>{content}</ThemeProvider>
    ) : (
      content
    );

  return (
    <Dialog
      open={open}
      maxWidth={maxWidth}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      style={forceOnTop ? { zIndex: NESTED_DIALOG_Z_INDEX } : undefined}
    >
      <DialogTitle id="dialog-title">{title}</DialogTitle>
      <DialogContent id="dialog-description" dividers>
        {renderedContent}
      </DialogContent>
      <DialogActions>
        <Button
          variant={defaultButton === "CANCEL" ? "contained" : "outlined"}
          autoFocus={defaultButton === "CANCEL"}
          focusRipple
          onClick={onCancelButtonClick}
          tabIndex={defaultButton === "CANCEL" ? 1 : 2}
          disabled={cancelButtonDisabled}
        >
          {cancelButtonText}
        </Button>
        <Button
          variant={defaultButton === "OK" ? "contained" : "outlined"}
          autoFocus={defaultButton === "OK"}
          focusRipple
          color={isWarning ? "error" : "primary"}
          onClick={onOKButtonClick}
          tabIndex={defaultButton === "OK" ? 1 : 2}
          disabled={okButtonDisabled}
        >
          {okButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalConfirmDialog;
